<template>
  <div class="home-page">
    <!-- 顶部横幅 -->
    <div class="hero-banner" :style="{ backgroundImage: 'url(' + heroBg + ')' }">
    </div>

    <!-- 论坛最新帖子 -->
    <div class="section-wrapper" v-if="forumPosts.length > 0">
      <div class="section-header">
        <h2 class="section-title">💬 论坛最新</h2>
        <a class="section-more" @click.prevent="$router.push('/forum')">
          更多 <span>→</span>
        </a>
      </div>
      <div class="forum-list">
        <div class="forum-item" v-for="post in forumPosts" :key="post.id" @click="goToForumPost(post.id)">
          <h4 class="forum-title">{{ post.title }}</h4>
          <p class="forum-meta">
            <span>{{ post.username }}</span>
            <span>{{ post.boardName }}</span>
            <span>👁 {{ post.viewCount }}</span>
            <span>👍 {{ post.likeCount }}</span>
            <span>💬 {{ post.commentCount }}</span>
            <span>{{ formatTime(post.createdTime) }}</span>
          </p>
        </div>
      </div>
    </div>

    <!-- 热度前十 -->
    <div class="section-wrapper" v-if="hotList.length > 0">
      <div class="section-header">
        <h2 class="section-title">🔥 热门模型</h2>
        <a class="section-more" @click.prevent="goToDatabase('hot')">
          更多 <span>→</span>
        </a>
      </div>
      <div class="model-grid">
        <div 
          class="model-card" 
          v-for="item in hotList" 
          :key="item.id"
          @click="goToDetail(item.id)"
        >
          <div class="card-image">
            <img :src="getDisplayImage(item.coverImage)" :alt="item.modelName" />
          </div>
          <div class="card-title">{{ item.modelName }}</div>
        </div>
      </div>
    </div>

    <!-- 最近更新前十 -->
    <div class="section-wrapper" v-if="latestList.length > 0">
      <div class="section-header">
        <h2 class="section-title">🆕 最新收录</h2>
        <a class="section-more" @click.prevent="goToDatabase('update_desc')">
          更多 <span>→</span>
        </a>
      </div>
      <div class="model-grid">
        <div 
          class="model-card" 
          v-for="item in latestList" 
          :key="item.id"
          @click="goToDetail(item.id)"
        >
          <div class="card-image">
            <img :src="getDisplayImage(item.coverImage)" :alt="item.modelName" />
          </div>
          <div class="card-title">{{ item.modelName }}</div>
        </div>
      </div>
    </div>

    <!-- 加载中 -->
    <div class="loading-wrapper" v-if="loading">
      <p>加载中...</p>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { getHotModels, getLatestModels, getForumLatest } from '@/api/home'
import heroBg from '@/assets/images/home.png'
import defaultLogo from '@/assets/images/logo.png'

export default {
  name: 'Home',
  data() {
    return {
      loading: false,
      hotList: [],
      latestList: [],
      forumPosts: [],
      heroBg,
      defaultLogo
    }
  },
  computed: {
    ...mapState(['siteName']),
    ...mapGetters(['isLoggedIn'])
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const [hotRes, latestRes, forumRes] = await Promise.all([
          getHotModels(),
          getLatestModels(),
          getForumLatest()
        ])
        if (hotRes.code === 200) this.hotList = hotRes.data || []
        if (latestRes.code === 200) this.latestList = latestRes.data || []
        if (forumRes && forumRes.code === 200) this.forumPosts = forumRes.data || []
      } catch (error) {
        console.error('加载首页数据失败:', error)
      } finally {
        this.loading = false
      }
    },

    isInvalidImageUrl(url) {
      if (!url) return true
      const invalidPatterns = [
        '83f89c28bad4321321c16fdd915c552a',
        'default_img.png',
        'default_error_img.jpg'
      ]
      return invalidPatterns.some(p => url.includes(p))
    },

    getDisplayImage(url) {
      if (this.isInvalidImageUrl(url)) return this.defaultLogo
      return url || this.defaultLogo
    },

    goToDetail(id) {
      if (!this.isLoggedIn) {
        window.location.href = '/login?redirect=/model/' + id
        return
      }
      window.location.href = '/model/' + id
    },

    goToForumPost(id) {
      this.$router.push('/forum/post/' + id)
    },

    goToDatabase(sort) {
      const url = '/database?sort=' + sort
      if (!this.isLoggedIn) {
        window.location.href = '/login?redirect=' + encodeURIComponent(url)
        return
      }
      this.$router.push(url).catch(() => {})
    },

    formatTime(time) {
      if (!time) return ''
      const d = new Date(time)
      const now = new Date()
      const diff = now - d
      if (diff < 3600000) return Math.floor(diff / 60000) + '分钟前'
      if (diff < 86400000) return Math.floor(diff / 3600000) + '小时前'
      if (diff < 604800000) return Math.floor(diff / 86400000) + '天前'
      return time.substring(0, 10)
    }
  }
}
</script>

<style scoped>
.home-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f8f9fc 0%, #f0f2f5 100%);
}

/* ============ 顶部横幅 ============ */
.hero-banner {
  height: 35vh;
  min-height: 280px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  background-color: #1a1a3e;
}

/* ============ 通用区块样式 ============ */
.section-wrapper {
  max-width: 1280px;
  margin: 40px auto;
  padding: 0 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-title {
  font-size: 22px;
  font-weight: 700;
  color: #333;
  margin: 0;
}

.section-more {
  font-size: 14px;
  color: #e60012;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.3s;
}

.section-more:hover {
  color: #c50010;
}

.section-more span {
  margin-left: 4px;
  transition: transform 0.3s;
}

.section-more:hover span {
  transform: translateX(4px);
}

/* ============ 模型网格 ============ */
.model-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
}

.model-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.25s ease;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.model-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.1);
  border-color: #e60012;
}

.model-card:active {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.08);
}

.card-image {
  aspect-ratio: 1;
  overflow: hidden;
  background: #f5f5f5;
  position: relative;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
  display: block;
}

.model-card:hover .card-image img {
  transform: scale(1.05);
}

.card-title {
  padding: 12px 14px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 44px;
}

/* ============ 论坛列表 ============ */
.forum-list {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.forum-item {
  padding: 14px 20px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.2s;
}

.forum-item:hover {
  background: #fafafa;
}

.forum-item:active {
  background: #f5f5f5;
}

.forum-item:last-child {
  border-bottom: none;
}

.forum-title {
  font-size: 15px;
  font-weight: 500;
  color: #333;
  margin: 0 0 6px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.forum-meta {
  font-size: 12px;
  color: #999;
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin: 0;
}

/* ============ 加载中 ============ */
.loading-wrapper {
  text-align: center;
  padding: 40px;
  color: #999;
}

.loading-wrapper p {
  margin: 0;
  font-size: 14px;
}

/* ============ 平板适配 (768px - 1024px) ============ */
@media (max-width: 1024px) {
  .model-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
  }
  
  .section-wrapper {
    margin: 30px auto;
    padding: 0 20px;
  }
}

/* ============ 移动端适配 (≤768px) ============ */
@media (max-width: 768px) {
  .hero-banner {
    height: 30vh;
    min-height: 160px;
    max-height: 220px;
  }
  
  .section-wrapper {
    margin: 24px auto;
    padding: 0 12px;
  }
  
  .section-header {
    margin-bottom: 14px;
  }
  
  .section-title {
    font-size: 17px;
  }
  
  .section-more {
    font-size: 13px;
  }
  
  /* 移动端模型网格：2列 */
  .model-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  
  .model-card {
    border-radius: 10px;
  }
  
  .card-title {
    padding: 10px;
    font-size: 13px;
    min-height: 40px;
    -webkit-line-clamp: 2;
  }
  
  /* 移动端论坛列表 */
  .forum-list {
    border-radius: 12px;
  }
  
  .forum-item {
    padding: 12px 14px;
  }
  
  .forum-title {
    font-size: 14px;
    margin-bottom: 4px;
  }
  
  .forum-meta {
    font-size: 11px;
    gap: 8px;
  }
  
  /* 优化触摸体验 */
  .model-card,
  .forum-item,
  .section-more {
    -webkit-tap-highlight-color: transparent;
  }
}

/* ============ 小屏手机适配 (≤375px) ============ */
@media (max-width: 375px) {
  .hero-banner {
    height: 25vh;
    min-height: 140px;
  }
  
  .section-title {
    font-size: 16px;
  }
  
  .model-grid {
    gap: 8px;
  }
  
  .card-title {
    padding: 8px;
    font-size: 12px;
    min-height: 36px;
  }
  
  .forum-meta {
    font-size: 10px;
    gap: 6px;
  }
  
  .forum-meta span {
    white-space: nowrap;
  }
}
</style>